import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

interface AdminUser {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
  organizationCount: number;
  createdAt: string;
}

interface UsersResponse {
  users: AdminUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

async function fetchUsers(page: number, search: string): Promise<UsersResponse> {
  const params = new URLSearchParams({ page: String(page), limit: "25" });
  if (search) params.set("search", search);

  const res = await fetch(`${API_BASE}/admin/users?${params}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to fetch users");
  return res.json();
}

export function AdminUsersPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", page, search],
    queryFn: () => fetchUsers(page, search),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="page-header page-header-primary">
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-indigo-200 mb-2">
            <Link to="/admin" className="hover:text-white">Admin</Link>
            <span>/</span>
            <span>Users</span>
          </div>
          <h1 className="text-3xl font-bold">All Users</h1>
          <p className="mt-1 text-indigo-100">
            {data?.pagination.total || 0} total users
          </p>
        </div>
        <div className="page-header-decor" />
        <div className="page-header-decor-alt" />
      </div>

      {/* Search */}
      <div className="card">
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email or name..."
            className="input input-primary flex-1"
          />
          <button type="submit" className="btn btn-primary">
            Search
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Loading...</div>
        ) : data?.users && data.users.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr className="table-header">
                    <th className="table-header-cell">User</th>
                    <th className="table-header-cell">Email Verified</th>
                    <th className="table-header-cell text-right">Organizations</th>
                    <th className="table-header-cell text-right">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((user) => (
                    <tr key={user.id} className="table-row">
                      <td className="table-cell">
                        <Link
                          to={`/admin/users/${user.id}` as any}
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          {user.name}
                        </Link>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                      </td>
                      <td className="table-cell">
                        {user.emailVerified ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-600">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                            </svg>
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="table-cell text-right">{user.organizationCount}</td>
                      <td className="table-cell text-right text-sm text-muted-foreground">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-4 py-3">
                <div className="text-sm text-muted-foreground">
                  Page {data.pagination.page} of {data.pagination.totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="btn btn-secondary btn-sm"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
                    disabled={page === data.pagination.totalPages}
                    className="btn btn-secondary btn-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            No users found
          </div>
        )}
      </div>
    </div>
  );
}
